import type { RoundInfo, UpcomingCustomInterview } from '../api/roundsApi';

export type QuickLaunchAction = 'retake' | 'continue' | 'start_new' | 'practice';

export type QuickLaunchRoundType = 'aptitude' | 'coding' | 'gd';

export interface QuickLaunchTarget {
  action: QuickLaunchAction;
  interviewId?: string;
  interviewName?: string;
  roundNum?: number;
  roundType?: QuickLaunchRoundType;
  roundConfig?: Record<string, unknown>;
  deadline?: string;
  needsAccept?: boolean;
}

export const QUICK_LAUNCH_PRACTICE_HINT =
  'Start a practice interview on your current resume and preference';

const ACTION_PRIORITY: Record<QuickLaunchAction, number> = {
  retake: 0,
  continue: 1,
  start_new: 2,
  practice: 3,
};

const formatDeadline = (deadline: string | undefined): string => {
  if (!deadline) {
    return 'the scheduled date';
  }
  const date = new Date(deadline);
  if (Number.isNaN(date.getTime())) {
    return deadline;
  }
  return date.toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

const isInterviewExpired = (deadline: string | undefined): boolean => {
  if (!deadline) {
    return false;
  }
  const deadlineDate = new Date(deadline);
  if (Number.isNaN(deadlineDate.getTime())) {
    return false;
  }
  return deadlineDate < new Date();
};

const classifyAction = (
  round: RoundInfo,
  roundStatus: string
): QuickLaunchAction => {
  if (roundStatus === 'abandoned') {
    return 'retake';
  }
  if (round.round_number === 1) {
    return 'start_new';
  }
  return 'continue';
};

/** Same logic as CandidateProfile upcoming tab — abandoned retake or pending next round only. */
export const findActionableRound = (
  interview: UpcomingCustomInterview
): RoundInfo | null => {
  if (!interview.accepted || interview.rounds.length === 0) {
    return null;
  }

  return (
    interview.rounds.find((round, idx) => {
      const status = round.status || 'pending';
      if (status === 'abandoned') {
        return true;
      }
      if (status !== 'pending') {
        return false;
      }
      return interview.rounds.slice(0, idx).every((prev) => prev.status === 'completed');
    }) ?? null
  );
};

const targetFromUnacceptedInterview = (
  interview: UpcomingCustomInterview
): QuickLaunchTarget | null => {
  if (interview.accepted || isInterviewExpired(interview.deadline)) {
    return null;
  }
  if (interview.jdProcessingStatus !== 'processed' || interview.rounds.length === 0) {
    return null;
  }

  const round = interview.rounds[0];
  const status = round.status || 'pending';
  if (round.round_number !== 1 || status !== 'pending') {
    return null;
  }

  return {
    action: 'start_new',
    interviewId: interview.id,
    interviewName: interview.name,
    roundNum: 1,
    roundType: round.type,
    roundConfig: round.config,
    deadline: interview.deadline,
    needsAccept: true,
  };
};

export const targetFromInterview = (
  interview: UpcomingCustomInterview
): QuickLaunchTarget | null => {
  if (isInterviewExpired(interview.deadline)) {
    return null;
  }

  const unacceptedTarget = targetFromUnacceptedInterview(interview);
  if (unacceptedTarget) {
    return unacceptedTarget;
  }

  if (!interview.accepted) {
    return null;
  }

  const round = findActionableRound(interview);
  if (!round) {
    return null;
  }

  const roundStatus = round.status || 'pending';

  return {
    action: classifyAction(round, roundStatus),
    interviewId: interview.id,
    interviewName: interview.name,
    roundNum: round.round_number,
    roundType: round.type,
    roundConfig: round.config,
    deadline: interview.deadline,
  };
};

export const practiceQuickLaunchTarget = (): QuickLaunchTarget => ({
  action: 'practice',
});

/** Pick the best quick-launch target; falls back to practice when none qualify. */
export const resolveQuickLaunchTarget = (
  interviews: UpcomingCustomInterview[]
): QuickLaunchTarget => {
  const candidates: Array<{
    target: QuickLaunchTarget;
    deadlineMs: number;
    index: number;
  }> = [];

  interviews.forEach((interview, index) => {
    const target = targetFromInterview(interview);
    if (!target || target.action === 'practice') {
      return;
    }
    const deadlineMs = interview.deadline
      ? new Date(interview.deadline).getTime()
      : Number.MAX_SAFE_INTEGER;
    candidates.push({
      target,
      deadlineMs: Number.isNaN(deadlineMs) ? Number.MAX_SAFE_INTEGER : deadlineMs,
      index,
    });
  });

  if (candidates.length === 0) {
    return practiceQuickLaunchTarget();
  }

  candidates.sort((a, b) => {
    const priorityDiff =
      ACTION_PRIORITY[a.target.action] - ACTION_PRIORITY[b.target.action];
    if (priorityDiff !== 0) {
      return priorityDiff;
    }
    if (a.deadlineMs !== b.deadlineMs) {
      return a.deadlineMs - b.deadlineMs;
    }
    return a.index - b.index;
  });

  const best = candidates[0];
  const tied = candidates.filter(
    (candidate) =>
      candidate.target.action === best.target.action &&
      candidate.deadlineMs === best.deadlineMs &&
      ACTION_PRIORITY[candidate.target.action] === ACTION_PRIORITY[best.target.action]
  );

  if (best.target.action === 'start_new' && tied.length > 1) {
    return tied[Math.floor(Math.random() * tied.length)].target;
  }

  return best.target;
};

export const buildQuickLaunchHint = (target: QuickLaunchTarget | null): string => {
  if (!target) {
    return QUICK_LAUNCH_PRACTICE_HINT;
  }

  if (target.action === 'practice') {
    return QUICK_LAUNCH_PRACTICE_HINT;
  }

  const name = target.interviewName || 'your interview';
  const roundNum = target.roundNum ?? 1;

  if (target.action === 'continue') {
    return `Continue with Round ${roundNum} of ${name} as you completed Round ${roundNum - 1} last time`;
  }

  if (target.action === 'retake') {
    return `Retake round ${roundNum} of ${name} which you might have been abandoned last time`;
  }

  if (target.action === 'start_new') {
    return `Start round 1 of ${name} reaching deadline by ${formatDeadline(target.deadline)}`;
  }

  return QUICK_LAUNCH_PRACTICE_HINT;
};

export const isPublishedQuickLaunchTarget = (target: QuickLaunchTarget | null): boolean =>
  Boolean(target && target.action !== 'practice' && target.interviewId);
